// cardholder-search.js / points-search.js: suggestions under the search box after 2 characters (200 ms debounce, the
// previous request cancelled), each a link to the cardholder.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export interface Suggestion {
  id: string;
  name: string;
  email: string;
  provider: string;
}

export function SearchSuggest({
  id,
  defaultValue,
  placeholder,
  suggest,
  linkTo,
}: {
  id: string;
  defaultValue: string;
  placeholder: string;
  suggest: (q: string, signal: AbortSignal) => Promise<Suggestion[]>;
  linkTo: (id: string) => string;
}) {
  const [query, setQuery] = useState(defaultValue);
  const [items, setItems] = useState<Suggestion[]>([]);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      suggest(q, controller.signal).then(
        (found) => setItems(found),
        () => {
          if (!controller.signal.aborted) setItems([]);
        },
      );
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, suggest]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setItems([]);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <div className="qc-search-wrap" ref={wrap}>
      <input
        id={id}
        type="search"
        name="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (e.target.value.trim().length < 2) setItems([]);
        }}
        placeholder={placeholder}
        autoComplete="off"
      />
      <div className="qc-search-suggestions" hidden={items.length === 0} role="listbox">
        {items.map((item) => (
          <Link key={item.id} to={linkTo(item.id)} className="qc-search-suggestion">
            <strong>{item.name}</strong>
            <span>
              {item.email} &middot; {item.provider}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
